import Bugsnag from "@bugsnag/js";
import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import CurrencyFormat from "react-currency-format";
import Marquee from "react-fast-marquee";

import {
  Button,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import constants from "../../Constants";
import PlacesAutocomplete from "../../components/common/PlacesAutocomplete";
import PriceMiniView from "../../components/common/PriceMiniView";
import PriceView from "../../components/common/PriceView";
import util from "../../util";
import reviews from "../articles/reviews";

const baseURL = constants.baseURL;

/* ─── Inline Styles (inspired by track-order) ─── */
const s = {
  page: {
    background: "linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%)",
    minHeight: "100vh",
    paddingTop: 30,
    paddingBottom: 60,
  },
  container: {
    maxWidth: 780,
    margin: "0 auto",
    padding: "0 16px",
  },
  /* header */
  header: {
    textAlign: "center",
    marginBottom: 24,
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#f4ad29",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 24,
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a2e",
    margin: 0,
  },
  headerSub: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 4,
  },
  /* stepper */
  stepper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    position: "relative",
    padding: "0 4px",
  },
  stepperLine: {
    position: "absolute",
    top: 16,
    left: 30,
    right: 30,
    height: 3,
    background: "#e5e7eb",
    borderRadius: 3,
    zIndex: 0,
    overflow: "hidden",
  },
  stepperProgress: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: "#f4ad29",
    borderRadius: 3,
    transition: "width 0.6s ease",
  }),
  stepDot: (active, done) => ({
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: done ? 16 : 13,
    fontWeight: 700,
    color: done ? "#fff" : active ? "#f4ad29" : "#9ca3af",
    background: done ? "#f4ad29" : active ? "#fff8e1" : "#f3f4f6",
    border: active && !done ? "2px solid #f4ad29" : "2px solid transparent",
    position: "relative",
    zIndex: 1,
    transition: "all 0.3s ease",
    boxShadow: done ? "0 2px 8px rgba(244,173,41,0.3)" : "none",
  }),
  stepLabel: (active, done) => ({
    fontSize: 11,
    fontWeight: active || done ? 600 : 400,
    color: active || done ? "#1a1a2e" : "#9ca3af",
    textAlign: "center",
    marginTop: 6,
    maxWidth: 80,
  }),
  stepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    cursor: "pointer",
  },
  /* card */
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#9ca3af",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  /* vehicle grid */
  vehicleGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 8,
  },
  vehicleCard: (picked) => ({
    width: "calc(33.33% - 6px)",
    maxWidth: 130,
    minWidth: 90,
    height: 100,
    padding: 6,
    border: picked ? "2px solid #f4ad29" : "1.5px solid #e5e7eb",
    borderRadius: 12,
    background: picked ? "#fff8e1" : "#fff",
    boxShadow: picked ? "0 2px 8px rgba(244,173,41,0.2)" : "0 1px 4px rgba(0,0,0,0.05)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  }),
  vehicleLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: "#374151",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  vehicleImg: {
    width: "100%",
    maxHeight: 60,
    objectFit: "contain",
  },
  viewToggle: {
    fontSize: 13,
    color: "#f4ad29",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 0",
    marginBottom: 4,
  },
  /* form */
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  formRowSingle: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 16,
    marginTop: 4,
  },
  formLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: "#6b7280",
    marginBottom: 6,
    display: "block",
  },
  formInput: {
    width: "100%",
    border: "1.5px solid #e0e0e0",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  /* extras pills */
  extrasRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    cursor: "pointer",
  },
  extrasPill: (highlight) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 20,
    background: highlight ? "#ecfdf5" : "#f3f4f6",
    border: highlight ? "1px solid #a7f3d0" : "1px solid #e5e7eb",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    transition: "all 0.2s",
  }),
  /* primary action button */
  primaryBtn: (disabled) => ({
    width: "100%",
    padding: "14px",
    borderRadius: 12,
    border: "none",
    background: disabled
      ? "#e5e7eb"
      : "linear-gradient(135deg, #f4ad29 0%, #e09a1a 100%)",
    color: disabled ? "#9ca3af" : "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.2s",
    boxShadow: disabled ? "none" : "0 4px 14px rgba(244,173,41,0.35)",
    marginTop: 8,
  }),
  /* price card */
  priceCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: 16,
    textAlign: "center",
  },
  tripBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 18px",
    borderRadius: 20,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: 13,
    color: "#555",
    marginTop: 12,
  },
  tripSep: {
    color: "#d1d5db",
    margin: "0 2px",
  },
  /* book now button */
  bookBtn: (disabled) => ({
    width: "100%",
    padding: "16px",
    borderRadius: 12,
    border: "none",
    background: disabled
      ? "#e5e7eb"
      : "linear-gradient(135deg, #34D1BF 0%, #2bb5a5 100%)",
    color: disabled ? "#9ca3af" : "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.2s",
    boxShadow: disabled ? "none" : "0 4px 14px rgba(52,209,191,0.35)",
  }),
  /* other options */
  otherGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "center",
  },
  /* reviews */
  reviewsHeading: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#9ca3af",
    textAlign: "center",
    marginBottom: 12,
  },
  /* help buttons */
  helpBar: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 24,
  },
  helpBtn: (bg) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    background: bg,
    border: "none",
    cursor: "pointer",
    transition: "transform 0.15s, opacity 0.2s",
  }),
  /* alert */
  alertError: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    padding: "10px 16px",
    color: "#991b1b",
    fontSize: 13,
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  /* spinner overlay */
  spinnerOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 0",
  },
  /* budget display */
  budgetHint: {
    fontSize: 12,
    color: "#059669",
    background: "#ecfdf5",
    padding: "6px 12px",
    borderRadius: 8,
    marginBottom: 6,
    display: "inline-block",
  },
};

/* ─── Booking Steps ─── */
const BOOKING_STEPS = [
  { key: "vehicle", label: "Vehicle", icon: "mdi-truck" },
  { key: "details", label: "Details", icon: "mdi-map-marker" },
  { key: "quote", label: "Quote", icon: "mdi-calculator" },
  { key: "book", label: "Book", icon: "mdi-check-circle" },
];

class Section extends Component {
  constructor() {
    super();

    let promocode = "";
    let vehicleId = "";
    let truckType = "";
    let justbook = "";
    let deliverylat = "";
    let collectiondate = "";
    let collectiontime = "";
    let description = "";
    let deliverylng = "";
    let collectionlat = "";
    let collectionlng = "";
    let category = "handheld-items";
    let runners = 1;
    let floors = 0;
    let prospectemail = "";
    let prospectmobilenumber = "";
    let quoteid = "";
    try {
      const queryParams = new URLSearchParams(window.location.search);
      promocode = queryParams.get("promocode") || "";
      truckType = queryParams.get("trucktype") || "1.5tonopen";
      justbook = queryParams.get("justbook") || "";
      description = queryParams.get("description") || "";
      deliverylat = queryParams.get("deliverylat") || "";
      deliverylng = queryParams.get("deliverylng") || "";
      collectiondate = queryParams.get("collectiondate") || "";
      collectiontime = queryParams.get("collectiontime") || "";
      collectionlat = queryParams.get("collectionlat") || "";
      collectionlng = queryParams.get("collectionlng") || "";
      category = queryParams.get("category") || "handheld-items";
      runners = queryParams.get("runners") || 1;
      floors = queryParams.get("floors") || 0;
      prospectemail = queryParams.get("prospectemail") || "";
      prospectmobilenumber = queryParams.get("prospectmobilenumber") || "";
      vehicleId = queryParams.get("v") || "";
      quoteid = queryParams.get("quoteid") || "";
    } catch (error) {
      Bugsnag.notify(error);
    }

    this.state = {
      quoteid,
      justbook,
      loading: false,
      success: false,
      trackYourOrder: false,
      openSuccessModal: false,
      truckType,
      acceptedOffer: "",
      apierror: "",
      orderid: "",
      collection: {
        lat: collectionlat,
        lng: collectionlng,
      },
      delivery: {
        lat: deliverylat,
        lng: deliverylng,
      },
      fullQuote: {
        tripDistance: 0,
        tripDuration: 0,
        sellingPrice: 0,
        storedQuoteId: "",
      },
      firstname: "",
      lastname: "",
      email: prospectemail,
      mobilenumber: prospectmobilenumber,
      collectiondate,
      collectiontime,
      description,
      duration: 0,
      trips: 0,
      prospectemail,
      prospectmobilenumber,
      vehicletypes: [],
      vehiclecategories: [],
      showOtherCategories: false,
      usercategory: category,
      vehicleconfigurations: {},
      openDiscountCode: false,
      promocode: promocode || "",
      vehicleId: vehicleId || "",
      runners,
      floors,
      quoteProfile: {},
      stories: [],
      expanded: [],
    };
  }

  componentDidMount() {
    this.getVehicleTypes();
    this.getQuoteProfile();
    if (this.validStepOne()) this.getQuote();
  }
  driverCancelOrder() {
    this.setState({
      loading: true,
      apierror: "",
    });
    alert("Job cancelled");
    const { quoteid, quotevehicleid } = this.state;
    axios
      .post(baseURL + "/driver-cancel-quote", {
        quoteid,
        quotevehicleid,
      })
      .then((response) => {
        window.location.href = "/quote?quoteid=" + quoteid;
      })
      .catch((e) => {
        console.log("/driver-cancel-order  err", e);
        alert(e);
      })
      .finally(() => {});
  }
  getQuoteProfile() {
    //setloading
    this.setState({
      loading: true,
    });

    const { quoteid, truckType } = this.state;

    if (quoteid?.length > 3)
      axios
        .post(baseURL + "/quote-profile", {
          quoteid: quoteid,
          source_:
            util.navigator_() + util.source_() + " qtref:" + document?.referrer,
          ...util.getAllGoogleClickIds(), // gclid, gbraid, wbraid for conversion tracking
        })
        .then((repos) => {
          const quoteProfile = repos.data;
          this.setState({
            loading: false,
            ...quoteProfile,
            mobilenumber: quoteProfile.prospectmobilenumber,
            prospectmobilenumber: quoteProfile.prospectmobilenumber,
            delivery: {
              lat: quoteProfile.deliverylat,
              lng: quoteProfile.deliverylng,
            },
            collection: {
              lat: quoteProfile.collectionlat,
              lng: quoteProfile.collectionlng,
            },
            firstname: quoteProfile.firstname || " ",
            lastname: quoteProfile.lastname || " ",
            email: quoteProfile.prospectemail || " ",
            prospectemail: quoteProfile.prospectemail || " ",
            budget: quoteProfile.budget / 100 || undefined,
            truckType: quoteProfile.trucktype || truckType,
            fullQuote: {
              tripDistance: quoteProfile.tripdistance,
              tripDuration: quoteProfile.duration,
              sellingPrice: quoteProfile.sellingprice,
              storedQuoteId: quoteProfile.quoteid,
              discount: quoteProfile.discount,
            },
          });
        });
  }

  getVehicleTypes() {
    const { vehicletypes } = this.state;
    if (vehicletypes?.length === 0)
      axios.get(baseURL + "/vehicle-types").then((repos) => {
        const vehicletypesdata = repos.data;
        this.setState({
          vehicletypes: vehicletypesdata?.vehicles,
          vehiclecategories: vehicletypesdata?.categories,
          vehicleconfigurations: vehicletypesdata?.configurations,
        });
      });
  }

  changeTruck(truckType) {
    this.setState(
      {
        truckType: truckType || "",
      },
      () => {
        if (window.document.getElementById("addresses"))
          window.document.getElementById("addresses").scrollIntoView();
      }
    );
  }
  validStepOne() {
    const { truckType, delivery, collection, prospectmobilenumber } =
      this.state;
    if (prospectmobilenumber.length === 0) return false;
    if (truckType === "") return false;
    if (delivery.lat === 0) return false;
    if (collection.lat === 0) return false;
    return !(
      collection.lat === delivery.lat && collection.lng === delivery.lng
    );
  }
  validStepTwo() {
    const {
      firstname,
      lastname,
      email,
      mobilenumber,
      collectiondate,
      collectiontime,
      description,
    } = this.state;

    return !(
      firstname.length === 0 ||
      lastname.length === 0 ||
      email.length === 0 ||
      mobilenumber.length === 0 ||
      collectiondate.length === 0 ||
      collectiontime.length === 0 ||
      description.length === 0
    );
  }
  getQuote() {
    const {
      truckType,
      delivery,
      collection,
      usercategory,
      promocode,
      runners,
      collectiondate, //required
      collectiontime, //required
      floors,
      description,
      duration,
      trips,
      prospectemail,
      prospectmobilenumber,
      budget,
    } = this.state;
    if (!this.validStepOne()) return;

    this.setState({
      loading: true,
      apierror: "",
      acceptedOffer: "",
      fullQuote: {
        tripDistance: 0,
        tripDuration: 0,
        sellingPrice: 0,
        storedQuoteId: "",
        discount: "",
      },
    });
    axios
      .post(baseURL + "/quote", {
        description,
        duration,
        trips,
        truckType: this.state.quoteProfile.trucktype || truckType,
        delivery,
        collection,
        category: usercategory,
        promocode: promocode,
        runners,
        floors,
        prospectemail,
        collectiondate, //required?
        collectiontime, //required?
        prospectmobilenumber,
        budget: budget * 100 || null,
        source_:
          util.navigator_() + util.source_() + " qtref:" + document?.referrer,
        quotevehicleid: this.state.quotevehicleid,
        ...util.getAllGoogleClickIds(), // gclid, gbraid, wbraid for conversion tracking
      })
      .then((response) => {
        if (response.data.sellingPrice > 0) {
          this.setState(
            {
              loading: false,
              fullQuote: {
                tripDistance: response.data.tripDistance,
                tripDuration: response.data.tripDuration,
                sellingPrice: response.data.sellingPrice,
                storedQuoteId: response.data.quoteId,
                discount: response.data.discount,
                otherOptions: response.data.otherOptions,
              },
            },
            () => {
              if (window.document.getElementById("mainquote"))
                window.document.getElementById("mainquote").scrollIntoView();
            }
          );
        }
      })
      .catch((e) => {
        this.setState({
          loading: false,
          apierror: "Check your addresses or vehicle type and try again.",
        });
        Bugsnag.notify(e);
      });
  }
  acceptQuote() {
    this.setState({
      loading: true,
      apierror: "",
    });
    const {
      fullQuote,
      firstname,
      lastname,
      prospectemail,
      mobilenumber,
      collectiondate,
      collectiontime,
      description,
      acceptedOffer,
    } = this.state;

    axios
      .post(baseURL + "/accept-quote", {
        fullQuote,
        firstname,
        lastname,
        email: prospectemail,
        mobilenumber,
        collectiondate,
        collectiontime,
        description,
        acceptedOffer: acceptedOffer?.toLowerCase(),
      })
      .then((response) => {
        this.setState(
          {
            loading: false,
            success: response.status === 200,
            openSuccessModal: true,
            orderid: response?.data?.orderid,
          },
          () => {
            if (response.status === 200 && response?.data?.orderid?.length > 0)
              cookie.save(
                "currentOrder",
                `email=${this.state.email}&orderid=${response.data.orderid}`,
                {
                  path: "/",
                  maxAge: 86400,
                  secure: true,
                }
              );
          }
        );
      })
      .catch((e) => {
        this.setState({
          loading: false,
          apierror: "Request failed. Please try again.",
        });
      });
  }
  vehiclePriceTip(vehicle, change) {
    function roundToNearestFifty(num) {
      return Math.round(num / 50) * 50;
    }
    let costOfDelivery =
      2000 * vehicle?.pricePerMeterCents + //cents
      1200 * vehicle?.pricePerSecondCents + //cents for 10 minutes
      vehicle?.handlingFeeCents; //cents
    let serviceFeePercentage = vehicle?.serviceFeePercentage * costOfDelivery;
    return roundToNearestFifty(
      ///hack for days /////!!!!!! needs review
      Math.round(
        ((costOfDelivery + serviceFeePercentage) * 1.15 * change || 1) / 100
      )
    );
  }
  getImage(trucktype) {
    try {
      return `https://media.qiktruck.co.za/${trucktype}.png`;
    } catch (error) {
      return "";
    }
  }
  getProfileImage(photo) {
    try {
      return `https://advertsmedia.qiktruck.co.za/${photo}`;
    } catch (error) {
      return "";
    }
  }
  expandVehicleView(str) {
    let arr = [...this.state.expanded];
    let index = arr.indexOf(str);
    if (index > -1) {
      arr.splice(index, 1);
    } else {
      arr.push(str);
    }
    this.setState({ expanded: arr });
  }
  getHappyDriver(num) {
    return (
      <img
        src={"https://media.qiktruck.co.za/happydriver" + (num + 1) + ".png"}
        alt=""
        className={"happydriver"}
      />
    );
  }

  getCurrentStep() {
    const { truckType, fullQuote, success } = this.state;
    if (success) return 3;
    if (fullQuote.sellingPrice > 0) return 2;
    if (truckType && this.state.collection.lat && this.state.delivery.lat && this.state.prospectmobilenumber) return 1;
    if (truckType) return 0;
    return -1;
  }

  renderStepper() {
    const currentIdx = this.getCurrentStep();
    const progressPct = currentIdx < 0 ? 0 : (currentIdx / (BOOKING_STEPS.length - 1)) * 100;
    return (
      <div style={s.stepper}>
        <div style={s.stepperLine}>
          <div style={s.stepperProgress(progressPct)} />
        </div>
        {BOOKING_STEPS.map((step, i) => {
          const done = i <= currentIdx;
          const active = i === currentIdx;
          return (
            <div key={step.key} style={s.stepItem}>
              <div style={s.stepDot(active, done)}>
                {done ? <i className="mdi mdi-check" /> : i + 1}
              </div>
              <div style={s.stepLabel(active, done)}>{step.label}</div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const {
      vehicletypes,
      promocode,
      runners,
      floors,
      fullQuote: { discount, otherOptions, sellingPrice },
      loading,
      truckType,
      collectionaddress,
      deliveryaddress,
    } = this.state;
    const quoteDisabled = !this.validStepOne() || loading;
    const bookDisabled = !this.validStepOne() || this.state.loading;
    return (
      <React.Fragment>
        <div id={"pagefragmenttop"}></div>
        {this.props.seo}
        <style>{`
          .dwp-input:focus { border-color: #f4ad29 !important; }
          .dwp-btn:hover:not(:disabled) { transform: translateY(-1px); }
          .dwp-help-btn:hover { transform: translateY(-1px); opacity: 0.9; }
          .dwp-vehicle:hover { border-color: #f4ad29 !important; background: #fffdf5 !important; }
          @media (max-width: 600px) {
            .dwp-form-row { grid-template-columns: 1fr !important; }
            .dwp-vehicle-grid > div { width: calc(33.33% - 6px) !important; min-width: 80px !important; }
          }
        `}</style>

        {/* ─── Success Modal ─── */}
        <Modal isOpen={this.state.success && this.state.openSuccessModal}>
          <ModalHeader>
            <i className={"mdi mdi-check orange-circle  order-received"}></i>
            Order Received
          </ModalHeader>
          <ModalBody>
            <ListGroup className="black-text">
              <ListGroupItem>
                <i className={"mdi mdi-check orange-circle order-received-list"}></i>
                A driver will be in touch shortly to confirm your order.
              </ListGroupItem>
              <ListGroupItem>
                <i className={"mdi mdi-email order-received-list"}></i>
                You will receive an email with your invoice shortly.
              </ListGroupItem>
              <ListGroupItem>
                <i className={"mdi mdi-cash-multiple order-received-list"}></i>
                You can pay now or after the delivery.
              </ListGroupItem>
              <ListGroupItem>
                <i className={"mdi mdi-cash-multiple order-received-list"}></i>
                Follow the link in your email to make a payment.
              </ListGroupItem>
              <ListGroupItem>
                <i className={"mdi mdi-account-alert order-received-list"}></i>
                Produce your ID when the driver arrives.
              </ListGroupItem>
              <ListGroupItem>
                <i className={"mdi mdi-check orange-circle order-received-list"}></i>
                Happy moving!
              </ListGroupItem>
            </ListGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() =>
                this.setState(
                  { openSuccessModal: false, trackYourOrder: true },
                  () => {
                    window.location.href =
                      "/track-order?paynow=yes&orderid=" +
                      this.state.orderid +
                      "&email=" +
                      this.state.email;
                  }
                )
              }
            >
              Okay
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── Discount / Options Modal ─── */}
        <Modal isOpen={this.state.openDiscountCode}>
          <ModalHeader>
            <i className={"mdi mdi-clipboard-text order-received"}></i>
            More options
          </ModalHeader>
          <ModalBody>
            <Label> Add Helpers </Label>
            <br />
            <span>
              How many people do you need to carry and load the goods? The
              driver may not be able to help.
            </span>
            <Input
              id="runners"
              name="runners"
              type="number"
              defaultValue={runners}
              min={0}
              onChange={(e) => {
                this.setState({ runners: parseInt(e.target.value, 10) });
              }}
            />
            <br />
            <br />
            <Label>Add Floors </Label>
            <br />
            <span>
              How many floors are we required to carry the goods? Add more
              information in your description later.
            </span>
            <Input
              id="floors"
              name="floors"
              type="number"
              min={0}
              defaultValue={floors}
              onChange={(e) => {
                this.setState({ floors: parseInt(e.target.value, 10) });
              }}
            />
            <br />
            <br />
            <Label>Discount code</Label>
            <br />
            <span>Lucky one! Our Discount codes are up to 30% off!</span>
            <Input
              id="promocode"
              name="promocode"
              type="promocode"
              defaultValue={promocode}
              placeholder="Type your discount code here |"
              onChange={(e) => {
                this.setState({ promocode: e.target.value.toUpperCase() });
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => this.setState({ openDiscountCode: false })}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => {
                this.getQuote();
                this.setState({ openDiscountCode: false });
              }}
            >
              Apply
            </Button>
          </ModalFooter>
        </Modal>

        <section style={s.page} id={"pagetop"}>
          <div style={s.container}>
            {/* ─── Header ─── */}
            <div style={s.header}>
              <div style={s.headerIcon}>
                <i className="mdi mdi-truck-fast"></i>
              </div>
              <h1 style={s.headerTitle}>Book a Delivery</h1>
              <p style={s.headerSub}>
                Get an instant quote and book your truck in minutes
              </p>
            </div>

            {/* ─── Step Counter ─── */}
            {this.renderStepper()}

            {/* ─── Loading ─── */}
            {loading && (
              <div style={s.spinnerOverlay}>
                <Spinner color="warning" />
              </div>
            )}

            {/* ═══ STEP 1: Vehicle Selection ═══ */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <i className="mdi mdi-truck" style={{ fontSize: 16, color: "#f4ad29" }}></i>
                Select Vehicle
              </div>

              <div className="dwp-vehicle-grid" style={s.vehicleGrid}>
                {vehicletypes
                  ?.filter((v) => v.type.indexOf("closed") === -1)
                  ?.map((v, index) => {
                    const image = this.getImage(v.type);
                    const picked = truckType === v.type;
                    return (
                      <div
                        key={index}
                        className="dwp-vehicle"
                        style={s.vehicleCard(picked)}
                        onClick={() => this.changeTruck(v.type)}
                      >
                        <span style={s.vehicleLabel}>
                          {v.type.replace("open", " ").replace("andtrailer", " ")}
                        </span>
                        <img src={image} alt={v.type} style={s.vehicleImg} />
                      </div>
                    );
                  })}
              </div>

              {this.state.viewmore ? (
                <>
                  <div className="dwp-vehicle-grid" style={s.vehicleGrid}>
                    {vehicletypes
                      ?.filter((v) => v.type.indexOf("open") === -1)
                      ?.map((v, index) => {
                        const image = this.getImage(v.type);
                        const picked = truckType === v.type;
                        return (
                          <div
                            key={index}
                            className="dwp-vehicle"
                            style={s.vehicleCard(picked)}
                            onClick={() => this.changeTruck(v.type)}
                          >
                            <span style={s.vehicleLabel}>
                              {v.type.replace("closed", " ").replace("andtrailer", " ")}
                            </span>
                            <img src={image} alt={v.type} style={s.vehicleImg} />
                          </div>
                        );
                      })}
                  </div>
                  <div
                    style={s.viewToggle}
                    onClick={() => this.setState({ viewmore: false })}
                  >
                    Show less <i className="mdi mdi-chevron-up"></i>
                  </div>
                </>
              ) : (
                <div
                  style={s.viewToggle}
                  onClick={() => this.setState({ viewmore: true })}
                >
                  Show closed vehicles <i className="mdi mdi-chevron-down"></i>
                </div>
              )}
            </div>

            {/* ═══ STEP 2: Trip Details ═══ */}
            <div style={s.card} id={"quotestart"}>
              <div style={s.cardTitle}>
                <i className="mdi mdi-map-marker-path" style={{ fontSize: 16, color: "#f4ad29" }}></i>
                Trip Details
              </div>

              {/* Addresses */}
              <div className="dwp-form-row" style={s.formRow}>
                <div>
                  <label style={s.formLabel} id={"addresses"}>
                    <i className="mdi mdi-circle" style={{ fontSize: 8, color: "#34D1BF", marginRight: 4 }}></i>
                    Collection
                  </label>
                  <PlacesAutocomplete
                    type="text"
                    className="form-control dwp-input"
                    id="collection"
                    placeholder={collectionaddress || "Collection address"}
                    required
                    autoComplete="new-password"
                    onPlaceSelected={(place) => {
                      if (!place?.geometry?.location?.lat()) {
                        Bugsnag.notify(
                          new Error("COLLECTION place not found: " + this.state.truckType)
                        );
                      }
                      this.setState({
                        collection: {
                          lat: place?.geometry?.location?.lat(),
                          lng: place?.geometry?.location?.lng(),
                        },
                      });
                    }}
                  />
                </div>
                <div>
                  <label style={s.formLabel}>
                    <i className="mdi mdi-square" style={{ fontSize: 8, color: "#ff6b6b", marginRight: 4 }}></i>
                    Delivery
                  </label>
                  <PlacesAutocomplete
                    type="text"
                    className="form-control dwp-input"
                    id="delivery"
                    placeholder={deliveryaddress || "Delivery address"}
                    required
                    autoComplete="new-password"
                    onPlaceSelected={(place) => {
                      if (!place?.geometry?.location?.lat()) {
                        Bugsnag.notify(
                          new Error("DELIVERY place not found: " + this.state.truckType)
                        );
                      }
                      this.setState({
                        delivery: {
                          lat: place?.geometry?.location?.lat(),
                          lng: place?.geometry?.location?.lng(),
                        },
                      });
                    }}
                  />
                </div>
              </div>

              {/* Mobile + Extras */}
              <div className="dwp-form-row" style={{ ...s.formRow, marginTop: 16 }}>
                <div>
                  <label style={s.formLabel}>Mobile Number</label>
                  <input
                    className="dwp-input"
                    style={s.formInput}
                    id="prospectmobilenumber"
                    name="prospectmobilenumber"
                    placeholder="e.g. 082 123 4567"
                    defaultValue={this.state.prospectmobilenumber}
                    onChange={(e) => {
                      this.setState({
                        prospectmobilenumber: e.target.value,
                        mobilenumber: e.target.value,
                      });
                    }}
                  />
                </div>
                <div>
                  <label style={s.formLabel}>Extras & Discounts</label>
                  <div
                    style={s.extrasRow}
                    onClick={() => this.setState({ openDiscountCode: true })}
                  >
                    <span style={s.extrasPill(false)}>
                      <i className="mdi mdi-account-multiple"></i>
                      {runners} Helper{runners === 1 ? "" : "s"}
                    </span>
                    <span style={s.extrasPill(false)}>
                      <i className="mdi mdi-office-building"></i>
                      {floors} Floor{floors === 1 ? "" : "s"}
                    </span>
                    {promocode?.length > 0 ? (
                      <span style={s.extrasPill(true)}>
                        <i className="mdi mdi-tag"></i>
                        {promocode}
                      </span>
                    ) : (
                      <span style={s.extrasPill(false)}>
                        <i className="mdi mdi-tag-plus"></i>
                        Code
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div className="dwp-form-row" style={{ ...s.formRow, marginTop: 16 }}>
                <div>
                  <label style={s.formLabel}>Collection Date</label>
                  <input
                    className="dwp-input"
                    style={s.formInput}
                    id="collectiondate1"
                    name="collectiondate1"
                    defaultValue={this.state.collectiondate}
                    type="date"
                    onChange={(e) => {
                      this.setState({ collectiondate: e.target.value });
                    }}
                  />
                </div>
                <div>
                  <label style={s.formLabel}>Time</label>
                  <input
                    className="dwp-input"
                    style={s.formInput}
                    id="collectiontime1"
                    name="collectiontime1"
                    defaultValue={this.state.collectiontime}
                    type="time"
                    onChange={(e) => {
                      this.setState({ collectiontime: e.target.value });
                    }}
                  />
                </div>
              </div>

              {/* Budget */}
              <div style={{ ...s.formRowSingle, marginTop: 16 }}>
                <div>
                  <label style={s.formLabel}>Customer Offer (Rands)</label>
                  {this.state.budget && (
                    <div style={s.budgetHint}>
                      <CurrencyFormat
                        value={this.state.budget}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"R"}
                        renderText={(value) => (
                          <span>
                            <i className="mdi mdi-check-circle" style={{ marginRight: 4 }}></i>
                            {value} offered
                          </span>
                        )}
                      />
                    </div>
                  )}
                  <input
                    className="dwp-input"
                    style={{ ...s.formInput, fontSize: 18, fontWeight: 700, borderColor: "#f6d793", borderWidth: 2 }}
                    id="budget"
                    name="budget"
                    type="number"
                    placeholder="Enter amount in Rands"
                    defaultValue={this.state.budget}
                    onChange={(e) => {
                      this.setState({ budget: e.target.value });
                    }}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginTop: 16 }}>
                <label style={s.formLabel}>What are we moving?</label>
                <textarea
                  className="dwp-input"
                  style={{ ...s.formInput, minHeight: 100, resize: "vertical" }}
                  id="description"
                  defaultValue={this.state.description}
                  placeholder="Tell us everything you are moving — names, sizes and quantity..."
                  onChange={(e) => {
                    this.setState({ description: e.target.value });
                  }}
                />
              </div>

              {/* Get Quotations Button */}
              <button
                className="dwp-btn"
                style={s.primaryBtn(quoteDisabled)}
                onClick={() => this.getQuote()}
                disabled={quoteDisabled}
              >
                <i className="mdi mdi-calculator" style={{ fontSize: 18 }}></i>
                Get Quotations
              </button>

              {this.state.loading === false &&
                this.state.apierror.length > 0 && (
                  <div style={s.alertError}>
                    <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 16 }}></i>
                    {this.state.apierror}
                  </div>
                )}
            </div>

            {/* ═══ STEP 3: Quote & Booking ═══ */}
            {sellingPrice > 0 && (
              <div style={s.priceCard} id={"mainquote"}>
                <div style={s.cardTitle}>
                  <i className="mdi mdi-calculator" style={{ fontSize: 16, color: "#f4ad29" }}></i>
                  Your Quote
                </div>

                <PriceView
                  sellingPrice={sellingPrice || 0}
                  discount={discount}
                  loading={loading}
                  disabled={
                    typeof sellingPrice === "undefined" ||
                    sellingPrice === null ||
                    sellingPrice === 0 ||
                    loading
                  }
                  trucktype={truckType}
                  showButton={false}
                  acceptOffer={() => {
                    console.log("acceptOffer");
                    this.setState({ acceptedOffer: "sellingPrice" }, () => {
                      if (window.document.getElementById("makebooking"))
                        window.document
                          .getElementById("makebooking")
                          .scrollIntoView();
                    });
                  }}
                />

                {/* Trip info badge */}
                {this.state.fullQuote.tripDistance > 0 && (
                  <div style={s.tripBadge}>
                    <i className="mdi mdi-map-marker-distance" style={{ color: "#f4ad29" }}></i>
                    <span>{Math.round(this.state.fullQuote.tripDistance / 1000)} km</span>
                    <span style={s.tripSep}>&middot;</span>
                    <i className="mdi mdi-clock-outline" style={{ color: "#f4ad29" }}></i>
                    <span>{Math.round(this.state.fullQuote.tripDuration / 60)} min</span>
                    <span style={s.tripSep}>&middot;</span>
                    <span>once-off trip</span>
                  </div>
                )}

                {/* Book Now */}
                <div style={{ marginTop: 20 }}>
                  <button
                    className="dwp-btn"
                    style={s.bookBtn(bookDisabled)}
                    onClick={() => this.acceptQuote()}
                    disabled={bookDisabled}
                  >
                    <i className="mdi mdi-check-circle" style={{ fontSize: 18 }}></i>
                    Book Now
                  </button>
                </div>
              </div>
            )}

            {/* ═══ Other Vehicle Options ═══ */}
            {otherOptions && (
              <div style={s.card}>
                <div style={s.cardTitle}>
                  <i className="mdi mdi-swap-horizontal" style={{ fontSize: 16, color: "#f4ad29" }}></i>
                  Other Vehicle Options
                </div>
                <div style={s.otherGrid}>
                  {otherOptions.map((option) => (
                    <PriceMiniView
                      key={option.truckType}
                      option={option}
                      disabled={
                        typeof sellingPrice === "undefined" ||
                        sellingPrice === null ||
                        sellingPrice === 0 ||
                        loading
                      }
                      getQuote={() => {
                        this.setState(
                          { truckType: option.truckType },
                          () => { this.getQuote(); }
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ═══ Reviews ═══ */}
            <div style={{ ...s.card, padding: "16px 24px" }}>
              <div style={s.reviewsHeading}>
                <i className="mdi mdi-star" style={{ color: "#fbbf24", marginRight: 4 }}></i>
                Customer Reviews
              </div>
              <Marquee gradient={true} gradientWidth={0} speed={6} direction="right">
                {reviews.map((a, index) => (
                  <div key={index}>
                    <div className="home-reviews">
                      <h1>{a.heading}</h1>
                      <div className="date-article-added">
                        <i className="mdi mdi-clock-outline ml-2"></i>
                        {a.date}
                        <i className="mdi mdi-pin ml-2"></i>
                        {a.location}
                        {Array.from({ length: a.category }).map((x, i) => (
                          <i className="mdi mdi-star" key={i}></i>
                        ))}
                      </div>
                      <p>{a.copy}</p>
                    </div>
                  </div>
                ))}
              </Marquee>
            </div>

            {/* ═══ Action Buttons ═══ */}
            <div style={s.helpBar}>
              <button
                className="dwp-help-btn"
                style={s.helpBtn("#34D1BF")}
                onClick={() => {
                  window.open("tel:" + this.state.prospectmobilenumber, "_blank");
                }}
                disabled={!this.validStepOne() || loading}
              >
                <i className="mdi mdi-phone"></i>
                Call Customer
              </button>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default Section;
